import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    Wallet, 
    ArrowUpCircle, 
    ArrowDownCircle, 
    Calendar,
    FileText,
    Loader2,
    Clock,
    TrendingUp,
    TrendingDown,
    Info,
    AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const WalletPage = () => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [wallet, setWallet] = useState({
        balance: 0,
        transactions: [],
        active_credits: []
    });

    useEffect(() => {
        fetchWallet();
    }, []);

    const fetchWallet = async () => {
        try {
            const response = await axios.get(`${API_URL}/account/wallet`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWallet(response.data);
        } catch (error) {
            console.error('Error fetching wallet:', error);
            toast.error('Erreur lors du chargement du wallet');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateShort = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getTransactionIcon = (type) => {
        if (type === 'credit') {
            return <ArrowUpCircle className="w-5 h-5 text-green-600" />;
        }
        return <ArrowDownCircle className="w-5 h-5 text-red-600" />;
    };

    const getTransactionBadge = (refType) => {
        switch (refType) {
            case 'sav':
                return <Badge className="bg-green-100 text-green-700">Avoir SAV</Badge>;
            case 'order':
                return <Badge className="bg-blue-100 text-blue-700">Commande</Badge>;
            case 'adjustment':
                return <Badge className="bg-amber-100 text-amber-700">Ajustement</Badge>;
            default:
                return <Badge variant="outline">{refType}</Badge>;
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 py-12 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            <Footer />
        </Layout>
        );
    }

    return (
        <Layout>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                        Mon Wallet
                    </h1>
                    <p className="text-slate-600 mt-1">
                        Gérez votre crédit boutique et consultez vos mouvements
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left column - Balance & Credits */}
                    <div className="space-y-6">
                        {/* Balance Card */}
                        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <Wallet className="w-8 h-8 text-blue-600" />
                                    </div>
                                    <p className="text-sm text-blue-600 font-medium mb-1">Solde disponible</p>
                                    <p className="text-4xl font-bold text-blue-700" data-testid="wallet-balance">
                                        {wallet.balance.toFixed(2)} €
                                    </p>
                                    <p className="text-xs text-slate-500 mt-3">
                                        Utilisable sur votre prochaine commande
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Active Credits */}
                        {wallet.active_credits.length > 0 && (
                            <Card className="border-slate-200">
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-green-600" />
                                        Avoirs actifs
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {wallet.active_credits.map((credit, idx) => (
                                        <div 
                                            key={idx} 
                                            className="p-3 bg-green-50 border border-green-100 rounded-lg"
                                            data-testid={`active-credit-${idx}`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <p className="font-medium text-green-800 text-sm">
                                                        {credit.reason || 'Avoir'}
                                                    </p>
                                                    <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                                                        <Clock className="w-3 h-3" />
                                                        Expire le {formatDateShort(credit.expires_at)}
                                                    </div>
                                                </div>
                                                <p className="font-bold text-green-700">
                                                    {credit.remaining_amount?.toFixed(2)} €
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                        {/* Info Card */}
                        <Card className="border-slate-200 bg-slate-50">
                            <CardContent className="pt-4">
                                <div className="flex gap-3">
                                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-slate-600">
                                        <p className="font-medium text-slate-900 mb-1">Comment ça marche ?</p>
                                        <ul className="space-y-1 text-xs">
                                            <li>• Votre crédit est automatiquement appliqué au checkout</li>
                                            <li>• Les avoirs SAV sont valables 12 mois</li>
                                            <li>• Le crédit le plus ancien est utilisé en premier</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right column - Transaction History */}
                    <div className="lg:col-span-2">
                        <Card className="border-slate-200">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5" />
                                    Historique des mouvements
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {wallet.transactions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <FileText className="w-8 h-8 text-slate-400" />
                                        </div>
                                        <p className="text-slate-500">Aucun mouvement pour le moment</p>
                                        <p className="text-sm text-slate-400 mt-1">
                                            Vos transactions apparaîtront ici
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {/* Table Header */}
                                        <div className="hidden md:grid grid-cols-12 gap-4 text-xs font-medium text-slate-500 uppercase px-4 py-2 border-b">
                                            <div className="col-span-3">Date</div>
                                            <div className="col-span-2">Type</div>
                                            <div className="col-span-3">Référence</div>
                                            <div className="col-span-2 text-right">Montant</div>
                                            <div className="col-span-2 text-right">Solde</div>
                                        </div>

                                        {/* Transactions */}
                                        {wallet.transactions.map((tx, idx) => (
                                            <div 
                                                key={tx.id || idx}
                                                className={`p-4 rounded-lg border transition-colors hover:border-slate-300 ${
                                                    tx.type === 'credit' ? 'bg-green-50/50 border-green-100' : 'bg-red-50/30 border-red-100'
                                                }`}
                                                data-testid={`transaction-${idx}`}
                                            >
                                                {/* Mobile view */}
                                                <div className="md:hidden space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            {getTransactionIcon(tx.type)}
                                                            {getTransactionBadge(tx.reference_type)}
                                                        </div>
                                                        <span className={`font-bold ${
                                                            tx.type === 'credit' ? 'text-green-700' : 'text-red-700'
                                                        }`}>
                                                            {tx.type === 'credit' ? '+' : '-'}{tx.amount.toFixed(2)} €
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-600">{tx.description}</p>
                                                    <div className="flex justify-between text-xs text-slate-500">
                                                        <span>{formatDate(tx.created_at)}</span>
                                                        <span>Solde: {tx.balance_after.toFixed(2)} €</span>
                                                    </div>
                                                </div>

                                                {/* Desktop view */}
                                                <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                                                    <div className="col-span-3 flex items-center gap-2">
                                                        {getTransactionIcon(tx.type)}
                                                        <span className="text-sm text-slate-600">
                                                            {formatDate(tx.created_at)}
                                                        </span>
                                                    </div>
                                                    <div className="col-span-2">
                                                        {getTransactionBadge(tx.reference_type)}
                                                    </div>
                                                    <div className="col-span-3">
                                                        <p className="text-sm font-medium text-slate-900">{tx.reference_number}</p>
                                                        <p className="text-xs text-slate-500 truncate">{tx.description}</p>
                                                    </div>
                                                    <div className={`col-span-2 text-right font-bold ${
                                                        tx.type === 'credit' ? 'text-green-700' : 'text-red-700'
                                                    }`}>
                                                        {tx.type === 'credit' ? '+' : '-'}{tx.amount.toFixed(2)} €
                                                    </div>
                                                    <div className="col-span-2 text-right">
                                                        <span className="text-sm font-medium text-slate-700">
                                                            {tx.balance_after.toFixed(2)} €
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        <Footer />
        </Layout>
    );
};

export default WalletPage;
